// SPEC-COLLECTION-OFFLINE-COMPLETE-01 (D-268e) PHASE_B 送信オーケストレーション。
//   PRIOR_ART = src/services/storeSync.js(巡回の送信エンジン)と同設計。第2の同期エンジンを作らない:
//   - probeOnline は storeSync のものを再利用(navigator.onLine は嘘をつく。GoTrue health で実到達性を見る)。
//   - in-flight ロックで多重送信(confirm 直後 + online + visibilitychange の競合)を潰す(storeSync inFlightByStore と同思想)。
//   - 成功(サーバ応答 ok)のときだけ markDraftSynced(B8)。失敗は setDraftLastError で未送信のまま(再送対象)。
//
//   FIX_B11 経路を1本にする: オンラインでも必ず先に collection_drafts へ書き、その後に本関数で送る。
//     confirm() は sendDraft を直接呼ぶ。アプリ起動時 / online / visibilitychange(B7 + FIX_B7_AMEND)は
//     flushCollectionDrafts を呼ぶ。両者は同一の sendDraft を通る(オンライン専用の直接送信経路を残さない)。
//   B8 冪等: 送信は draft.idempotency_key を fn_save_collection(D-267)に渡す。途中で落ちて再送しても
//     RPC がリプレイして二重伝票を作らない(新しい collection_id を採番しても既存 id が返る)。
import { probeOnline } from './storeSync'
import { isOfflineContinuation } from '../lib/auth/offlineGate' // D-304: 圏外継続中は集金送信を skip(失敗扱いにしない)
import {
  saveCollection, nextCollectionId,
  uploadCustomerSignature, uploadReceiptPhoto,
  // SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 一括計数の店舗レシート(1 枚)。
  uploadBulkReceiptPhoto,
} from './collections'
import {
  getCompletedUnsyncedDrafts,
  markDraftSynced,
  setDraftLastError,
  setDraftCollectionId,
  getDraftById,
} from '../lib/localStore/collectionDrafts'
import { notifyLfChange } from '../hooks/useUnsentBanner'
import { logger } from '../lib/logger'

const LOG_ERR_SEND = 'ERR-LF1-COLLECTION-SEND'

// FIX_B15: 送信中の localId を集合で保持し、sendDraft の入口で既に送信中なら即 skip する。
//   confirm 経由(直接 sendDraft)も flush 経由も同じ入口を通るため、両経路が同一ロックに従う。
//   これで「弱電波で送信が長引く最中に visibilitychange の flush が同じドラフトを掴む」二重送信(署名二重 upload)を防ぐ。
const _sendingIds = new Set()

/**
 * 1 ドラフトを送信する。オンライン前提(呼び出し側 flush が probe 済み、confirm は押下=送る意思)。
 * 手順: collection_id 採番 → 署名 upload → レシート upload → fn_save_collection(冪等キー付き)。
 *   成功で markDraftSynced(B8)。署名 upload 失敗は送信全体を失敗として未送信のまま(次回再送)。
 *   レシート upload 失敗は非致命(「写真なしで続行可」=オンライン挙動と同じ)、その写真だけ欠けて続行。
 * @returns {Promise<{ok:boolean, collectionId?:string, errCode?:string}>}
 */
export async function sendDraft(draft, { staff } = {}) {
  if (!draft || !draft.localId) return { ok: false, errCode: 'no-draft' }
  // D-304 IMPL_6: 圏外継続中は送信しない(flush 経由でなく confirm() から直接呼ばれる経路 FIX_B11 もここで止める)。
  //   圏外は失敗ではないため setDraftLastError を呼ばない・_sendingIds に add しない。
  if (isOfflineContinuation()) return { ok: false, errCode: 'offline-continuation' }
  const { localId, store_code: storeCode, collected_at: collectedAt } = draft
  // FIX_B15: 既に同一 localId を送信中なら二重送信しない(署名二重 upload・後着失敗の誤エラー記録を防ぐ)。
  if (_sendingIds.has(localId)) return { ok: false, errCode: 'in-flight' }
  _sendingIds.add(localId)
  try {
    // 1) collection_id を決める。FIX_B16: 採番は1ドラフト1回きり。既にドラフトへ焼き付けてあれば使い回す(再送で採番し直さない)。
    //    初回のみ nextCollectionId で採番し、署名 upload の前にドラフトへ焼き付ける(途中で落ちても次回同じ id を使う=孤児を増やさない)。
    //    圏外なら nextCollectionId が throw → 未送信のまま(AC2/AC3)。fn_save_collection は idempotency_key で二重を弾く(B8)。
    let collectionId = draft.collection_id
    if (!collectionId) {
      const stored = await getDraftById(localId)
      collectionId = stored?.collection_id
    }
    if (!collectionId) {
      collectionId = await nextCollectionId(storeCode, collectedAt)
      await setDraftCollectionId(localId, collectionId)
    }

    // 2) 先方署名を Storage へ。署名は必須(confirm 到達=署名済)なので失敗は送信全体の失敗として扱う。
    let customerSigUrl = null, customerSigPath = null
    if (draft.signatureDataUrl) {
      const { data: sig, error: sigErr } = await uploadCustomerSignature({ collectionId, dataUrl: draft.signatureDataUrl })
      if (sigErr) throw sigErr
      customerSigUrl = sig?.url ?? null
      customerSigPath = sig?.path ?? null
    }

    // 3) レシート写真(任意)を Storage へ。payload に url/path を差し込む。失敗はその写真だけ諦めて続行(非致命)。
    const payload = {}
    for (const [boothCode, row] of Object.entries(draft.payload ?? {})) {
      payload[boothCode] = { ...row }
    }
    for (const [boothCode, blob] of Object.entries(draft.receipts ?? {})) {
      if (!blob) continue
      try {
        const { data: up, error: upErr } = await uploadReceiptPhoto({ collectionId, boothCode, fileBlob: blob })
        if (upErr) throw upErr
        if (payload[boothCode]) { payload[boothCode].receipt_photo_url = up.url; payload[boothCode].receipt_photo_path = up.path }
      } catch (rcErr) {
        logger.warn?.(LOG_ERR_SEND, { localId, boothCode, phase: 'receipt', message: rcErr?.message })
      }
    }

    // SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 一括計数の店舗レシート(1 枚)。
    //   ★ブース別と同じ扱い。失敗してもその写真だけ諦めて続行する(非致命)。
    //   ★itemized のドラフトには bulkReceipt が無いので、この枝は 1 度も通らない。
    let bulkReceiptUrl = draft.bulk_receipt_photo_url ?? null
    let bulkReceiptPath = draft.bulk_receipt_photo_path ?? null
    if (draft.bulkReceipt) {
      try {
        const { data: up, error: upErr } = await uploadBulkReceiptPhoto({ collectionId, fileBlob: draft.bulkReceipt })
        if (upErr) throw upErr
        bulkReceiptUrl = up.url
        bulkReceiptPath = up.path
      } catch (rcErr) {
        logger.warn?.(LOG_ERR_SEND, { localId, boothCode: '_store', phase: 'receipt', message: rcErr?.message })
      }
    }

    // 4) 親+明細を単一トランザクションの RPC(冪等キー付き)で保存。オンライン経路と完全に同じ saveCollection を通す。
    const { data, error } = await saveCollection({
      storeCode,
      collectedAt,
      prevCollectionDate: draft.prev_collection_date || null,
      collectedBy: draft.staff_id ?? staff?.staffId ?? null,     // B4/B14: 記録上の入力者はドラフトが持つ(端末混在)
      collectedByName: draft.staff_name ?? staff?.staffName ?? null,
      booths: draft.booths ?? [],
      rowData: payload,
      collectionId,
      customerSignatureUrl: customerSigUrl,
      customerSignaturePath: customerSigPath,
      customerSignedAt: draft.customer_signed_at || null,
      idempotencyKey: draft.idempotency_key, // B3/B8: 同一ドラフトは常に同一キー(再送安全)
      // SPEC-COLLECTION-BULK-COUNT-MODE-01 (D-689): 数え方はドラフトが持つ。
      //   ★渡さなければ 'itemized' 既定で今までと同じ(通常入力のドラフトは 1 ビットも変わらない)。
      //   ★圏外で確定して翌日送信しても、確定したときの数え方で保存される。
      countingMode: draft.counting_mode || 'itemized',
      bulkCounts: draft.bulk_counts || null,
      bulkReceiptPhotoUrl: bulkReceiptUrl,
      bulkReceiptPhotoPath: bulkReceiptPath,
      // SPEC-PROFIT-SHARE-SERVER-SNAPSHOT-V1 (D-487): 分配 3 列の受け渡しは廃止した。
      //   焼き付けはサーバが行を書く瞬間に行う。圏外で確定して翌日送信する場合、率は
      //   「送信時のマスタ値」になる(D-476 は確定時点の値だった)。ひろ確定で集金時に率は
      //   変わらず契約変更はマスタ変更で行うため、この差は運用上生じない。
    })
    if (error) {
      logger.error?.(LOG_ERR_SEND, { localId, phase: 'save', message: error?.message })
      await setDraftLastError(localId, 'save-failed').catch(() => {})
      return { ok: false, errCode: 'save-failed' }
    }
    const savedId = data?.collectionId ?? collectionId
    await markDraftSynced(localId, savedId) // B8: サーバ成功後にのみ synced を立てる
    return { ok: true, collectionId: savedId }
  } catch (err) {
    logger.error?.(LOG_ERR_SEND, { localId, phase: 'send', message: err?.message })
    await setDraftLastError(localId, 'exception').catch(() => {})
    return { ok: false, errCode: 'exception' }
  } finally {
    _sendingIds.delete(localId) // FIX_B15: 送信完了で localId ロックを解放
  }
}

// FIX_D9(純関数): flush の結果(sent/failed/skipped)を人間向けメッセージに変える。**「何も起きなかった」経路を残さない**
//   (GOAL の4つ目「何が起きているか説明が無い」対策)。圏外(skipped)・一部失敗・全失敗・成功をすべて言い分ける。
export function describeFlushResult({ sent = 0, failed = 0, skipped = 0 } = {}) {
  if (sent > 0 && failed === 0) return { msg: `${sent}件送信しました`, type: 'success' }
  if (sent > 0 && failed > 0) return { msg: `${sent}件送信・${failed}件失敗(未送信は端末に残っています)`, type: 'error' }
  if (failed > 0) return { msg: `送信に失敗しました(${failed}件・未送信は端末に残っています)`, type: 'error' }
  if (skipped > 0) return { msg: '圏外のため送信できませんでした(オンライン復帰時に自動送信します)', type: 'error' }
  return { msg: '未送信はありません', type: 'success' }
}

// 多重送信防止: 全ドラフト flush はアプリ内に1本だけ走らせる(confirm 直後 + online + visibilitychange の競合対策)。
let _inFlight = null
export function _resetCollectionSyncInFlight() { _inFlight = null; _sendingIds.clear() }

/**
 * 完了・未送信ドラフト(B12)を全て送信する。B7 + FIX_B7_AMEND の発火点(起動 / online / visibilitychange)から呼ぶ。
 *   probeOnline で実到達性を見てから送る(圏外なら skip=未送信のまま)。同時呼び出しは in-flight Promise を共有。
 * @returns {Promise<{sent:number, failed:number, skipped:number}>}
 */
export function flushCollectionDrafts({ staff, skipProbe = false } = {}) {
  if (_inFlight) return _inFlight
  const run = _runFlush({ staff, skipProbe }).finally(() => { _inFlight = null })
  _inFlight = run
  return run
}

async function _runFlush({ staff, skipProbe = false } = {}) {
  const drafts = await getCompletedUnsyncedDrafts()
  if (!drafts.length) return { sent: 0, failed: 0, skipped: 0 }

  // D-304 IMPL_6: 圏外継続中は送信せず skip。既存の圏外 skip と同じ形 → describeFlushResult がそのまま圏外の文言を出す。
  if (isOfflineContinuation()) return { sent: 0, failed: 0, skipped: drafts.length }

  if (!skipProbe) {
    const ok = await probeOnline()
    if (!ok) return { sent: 0, failed: 0, skipped: drafts.length }
  }

  let sent = 0, failed = 0
  for (const d of drafts) {
    const res = await sendDraft(d, { staff })
    if (res?.ok) sent++
    else failed++
  }
  const ctx = { sent, failed }
  if (failed > 0) logger.error?.('LF1_COLLECTION_FLUSH_DONE', ctx)
  else logger.info?.('LF1_COLLECTION_FLUSH_DONE', ctx)
  notifyLfChange() // バナー等の再集計(PHASE_D で購読)。件数が減ったことを知らせる。
  return { sent, failed, skipped: 0 }
}
